import { type ReactElement } from 'react';
import {
  Form,
  Link,
  redirect,
  replace,
  useActionData,
  useLoaderData,
  useLocation,
} from 'react-router';
import type { ActionFunctionArgs, LoaderFunctionArgs } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Alert, Button } from '@marketplace/ui';
import {
  BookingSchema,
  type Booking,
  type BookingPayment,
  type BookingStatus,
  type MoneyDocumentSummary,
} from '@marketplace/contracts';
import { localeOf } from '../features/auth/actions.js';
import { documentPath, loadBookingDocuments } from '../features/billing/documents/documents.js';
import { CallOutCheckout } from '../features/checkout/CallOutCheckout.js';
import { bookingPath, type BookingReturn } from '../features/checkout/paths.js';
import {
  bookingErrorKey,
  bookingStateKey,
  type BookingErrorKey,
  type BookingStateKey,
} from '../features/checkout/payment-errors.js';
import { useWatch, type WatchWindow } from '../features/payouts/watch.js';
import { ApiError } from '../shared/api.js';
import { routeContext } from '../shared/query.js';
import { loadSession } from '../shared/session.js';

/**
 * `/:lang/bookings/:id` — a booking, and where its payment lands. `W5-T15` §3.2.
 *
 * **The status is the booking's, as `GET /api/bookings/{id}` answers it.** Never the URL's: Stripe
 * appends `redirect_status` to a return, and the page reads none of what it appends (AC 20). Never
 * Stripe.js's either: only the webhook makes a booking `PAID` (`W5-T02` AC 22).
 *
 * **`?return=checkout` or `?return=stripe` means the client has just confirmed**, so the page re-reads
 * the booking every two seconds for a minute while it is `PENDING_PAYMENT`, and says *we are
 * confirming*. After that, or on a later visit, a pending booking is *not received yet* with **Pay
 * now**; an expired one has **Book again**. Both post `booking.create` for the booking's quote, which
 * answers the live booking's intent or makes a new booking, and mount the checkout in place.
 *
 * **Once the reader has a provisional receipt or statement for the booking**, a line links to each
 * (`W5-T09` §4.9, AC 37). A failed read of them shows no line, never an error page.
 *
 * Owned by `agent-money` outside the literal `owns:`, on `account-payouts.tsx`'s precedent (§8.2).
 */
type BookingData =
  | { locale: string; view: 'notFound' }
  | {
      locale: string;
      view: 'booking';
      booking: Booking;
      watch: boolean;
      documents: readonly MoneyDocumentSummary[];
    };

type BookingActionData = { payment: BookingPayment } | { failure: BookingErrorKey };

/** Two seconds, for a minute: a webhook usually lands in a few. */
const BOOKING_WATCH: WatchWindow = { everyMs: 2_000, forMs: 60_000 };

interface BookingView {
  status: BookingStateKey | 'booking.pay.notConfirmed';
  offer: 'booking.pay.payNow' | 'booking.pay.bookAgain' | null;
}

/**
 * §3.2's table. While watching, a pending booking is *we are confirming* and offers nothing. After,
 * it is *not received yet* with Pay now; an expired one has Book again; the rest is settled.
 */
function viewOf(status: BookingStatus, watching: boolean): BookingView {
  if (status === 'PENDING_PAYMENT') {
    return watching
      ? { status: 'booking.pay.pending', offer: null }
      : { status: 'booking.pay.notConfirmed', offer: 'booking.pay.payNow' };
  }
  return {
    status: bookingStateKey(status),
    offer: status === 'EXPIRED' ? 'booking.pay.bookAgain' : null,
  };
}

/** The deadline as a clock time, in the reader's locale and their own time zone. */
function formatTime(iso: string, locale: string): string {
  return new Intl.DateTimeFormat(locale, { timeStyle: 'short' }).format(new Date(iso));
}

function returnOf(value: string | null): BookingReturn | undefined {
  return value === 'checkout' || value === 'stripe' ? value : undefined;
}

export async function loader({
  params,
  request,
  context,
}: LoaderFunctionArgs): Promise<BookingData> {
  const locale = localeOf(params);

  const session = await loadSession(context);
  if (session === null) throw redirect(`/${locale}/login`);

  const id = params['id'] ?? '';
  // A malformed id is the API's 404 too; there is no request worth sending for it.
  if (!BookingSchema.shape.id.safeParse(id).success) return { locale, view: 'notFound' };

  // Stripe's return carries `payment_intent`, `redirect_status` and the intent's **client secret**.
  // None of it is read, and a replace takes it out of the address bar and the history stack, so
  // it is not left in a bookmark, a shared link or a `Referer` (AC 21).
  const url = new URL(request.url);
  const from = returnOf(url.searchParams.get('return'));
  if (url.search !== (from === undefined ? '' : `?return=${from}`)) {
    throw replace(bookingPath(locale, id, from));
  }

  const { api } = context.get(routeContext);
  try {
    // Not cached: the page exists to show the latest answer, and the watch re-runs this loader.
    const [booking, documents] = await Promise.all([
      api.getBooking(id),
      loadBookingDocuments(context, id),
    ]);
    return { locale, view: 'booking', booking, watch: from !== undefined, documents };
  } catch (error) {
    if (error instanceof ApiError && error.status === 404) return { locale, view: 'notFound' };
    if (error instanceof ApiError && error.status === 401) throw redirect(`/${locale}/login`);
    throw error;
  }
}

export async function action({
  params,
  request,
  context,
}: ActionFunctionArgs): Promise<BookingActionData> {
  const locale = localeOf(params);
  const quoteId = (await request.formData()).get('quoteId');
  if (typeof quoteId !== 'string' || !BookingSchema.shape.quoteId.safeParse(quoteId).success) {
    return { failure: 'booking.error.notFound' };
  }

  const { api } = context.get(routeContext);
  try {
    return { payment: await api.createBooking(quoteId) };
  } catch (error) {
    if (!(error instanceof ApiError)) throw error;
    if (error.status === 401) throw redirect(`/${locale}/login`);
    return { failure: bookingErrorKey(error) };
  }
}

export function Component(): ReactElement {
  const { t } = useTranslation();
  const data = useLoaderData<BookingData>();
  const location = useLocation();

  if (data.view === 'notFound') {
    return (
      <article data-testid="booking">
        <h1>{t('booking.page.title')}</h1>
        <Alert tone="error">{t('booking.error.notFound')}</Alert>
      </article>
    );
  }
  // Keyed by the navigation: arriving again from a confirm (a second card, after the first watch ran
  // out) starts a new minute. The watch's own re-reads are revalidations and keep the key.
  return (
    <BookingPage
      key={location.key}
      locale={data.locale}
      booking={data.booking}
      watch={data.watch}
      documents={data.documents}
    />
  );
}

function BookingPage({
  locale,
  booking,
  watch,
  documents,
}: {
  locale: string;
  booking: Booking;
  watch: boolean;
  documents: readonly MoneyDocumentSummary[];
}): ReactElement {
  const { t } = useTranslation();
  const answer = useActionData<BookingActionData>();

  const pending = booking.status === 'PENDING_PAYMENT';
  const open = useWatch(watch && pending, BOOKING_WATCH);
  const watching = watch && pending && open;
  const { status, offer } = viewOf(booking.status, watching);
  const payment = answer !== undefined && 'payment' in answer ? answer.payment : null;
  const failure = answer !== undefined && 'failure' in answer ? answer.failure : null;

  return (
    <article data-testid="booking">
      <h1>{t('booking.page.title')}</h1>
      <div role="status" aria-live="polite">
        <p className="mp-lead" data-testid="booking-status">
          {t(status)}
        </p>
      </div>
      {offer === 'booking.pay.payNow' ? (
        <p>{t('checkout.payBy', { time: formatTime(booking.expiresAt, locale) })}</p>
      ) : null}
      {payment !== null ? (
        <CallOutCheckout payment={payment} locale={locale} />
      ) : offer === null ? null : (
        <Form method="post">
          <input type="hidden" name="quoteId" value={booking.quoteId} />
          {failure === null ? null : <Alert tone="error">{t(failure)}</Alert>}
          <Button variant="primary" type="submit">
            {t(offer)}
          </Button>
        </Form>
      )}
      {watching ? null : (
        <p>
          <Link to={`/${locale}/jobs/${booking.jobId}`}>{t('booking.pay.toJob')}</Link>
        </p>
      )}
      {documents.length === 0 ? null : (
        <ul data-testid="booking-documents">
          {documents.map((document) => (
            <li key={document.id}>
              <Link to={documentPath(locale, document.id)}>
                {t('documents.booking.link', {
                  kind: t(`documents.kind.${document.kind}`),
                  number: document.number,
                })}
              </Link>
            </li>
          ))}
        </ul>
      )}
    </article>
  );
}
